/**
 * EditableValueInput — schema-aware property input for UpdateActionEditForm.
 *
 * Renders the appropriate editable input for a property value:
 * - Stage properties → AssociatedPipelineStageInput (pipeline/lifecycle dropdown)
 * - Schema-backed properties → PropertyInput (currency, date, select, etc.)
 * - Unknown properties → plain text input fallback
 */

import { useCallback } from 'react';

import { CrmFieldValueInput } from '../../lib/crm/components/fields/CrmFieldValueInput';
import type { StageAssociationValue } from '../../lib/crm/components/properties/inputs/AssociatedPipelineStageInput';
import type { PropertyTypeSchema } from '../../lib/crm/hooks/usePropertySchema';
import { formatPropertyLabel } from './shared';

export interface EditableValueInputProps {
  propertyKey: string;
  isStage: boolean;
  stageAssociationType: string | undefined;
  entityTypeName: string;
  propSchema: PropertyTypeSchema | undefined;
  editValue: unknown;
  onChange: (key: string, value: unknown) => void;
  onStageChange: (key: string, assoc: StageAssociationValue | null) => void;
}

/**
 * Renders the appropriate editable input for a property value:
 * - Stage properties → AssociatedPipelineStageInput (pipeline/lifecycle dropdown)
 * - Schema-backed properties → PropertyInput (currency, date, select, etc.)
 * - Unknown properties → plain text input fallback
 */
export function EditableValueInput({
  propertyKey,
  isStage,
  stageAssociationType,
  entityTypeName,
  propSchema,
  editValue,
  onChange,
  onStageChange,
}: EditableValueInputProps) {
  // Stable callbacks to avoid re-triggering AssociatedPipelineStageInput's
  // auto-select effect (onChange is in its useEffect dependency array).
  const handleStageSelect = useCallback(
    (assoc: StageAssociationValue | null) => {
      onStageChange(propertyKey, assoc);
    },
    [onStageChange, propertyKey]
  );

  const handlePropertyChange = useCallback(
    (v: unknown) => {
      onChange(propertyKey, v);
    },
    [onChange, propertyKey]
  );

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(propertyKey, e.target.value || null);
    },
    [onChange, propertyKey]
  );

  if (isStage && stageAssociationType) {
    return (
      <CrmFieldValueInput
        kind='stage'
        value={(editValue as string) ?? null}
        onChange={handleStageSelect}
        entityTypeName={entityTypeName}
        associationTypeName={stageAssociationType}
        ariaLabel={`New value for ${formatPropertyLabel(propertyKey)}`}
        disableAutoSelect={editValue != null}
      />
    );
  }

  if (propSchema) {
    // Compact via the size prop, not descendant selectors: `size='xs'` is the
    // shared scale's compact step, so the control's type, padding and icon
    // dimensions move together. The old
    // `[&_input]:text-sm [&_select]:text-sm …` only reached controls whose
    // font class sits on an input/select/textarea — Select puts its on an
    // inner span, so it silently stayed at the larger size.
    return (
      <div className='text-sm'>
        <CrmFieldValueInput
          kind='property'
          schema={propSchema}
          value={editValue as string | number | object | null | undefined}
          onChange={handlePropertyChange}
          size='xs'
        />
      </div>
    );
  }

  return (
    <input
      type='text'
      value={
        editValue !== null && editValue !== undefined ? String(editValue) : ''
      }
      onChange={handleTextChange}
      aria-label={`New value for ${formatPropertyLabel(propertyKey)}`}
      className='w-full rounded-md border border-brand-300 bg-bg-primary px-2.5 py-1.5 text-sm text-fg-primary focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent'
    />
  );
}
